import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Play } from 'lucide-react';
import {
  groupArchivesByMatchId,
  listLscArchive,
  listLscLiveDashboard,
  listLscMatches,
  listLscMembers,
  LscApiError,
  mapLiveDashboardRowToLeagueGame,
  resolveLiveStreamKey,
} from '../../api';
import { resolveTeamVisual } from '../../cfbBranding/teamVisualResolver';
import { LEAGUE_SCHEDULES_PAGE_COPY, formatMatchIdentities } from '../../leagueNav';
import { broadcasterDisplayName } from '../../stream/broadcasterName';
import { formatReplayDuration } from '../../stream/replayDuration';
import { carryStreamSearch, isMemberPreview } from '../../stream/streamPages';
import { lscPath } from '../../routing';
import { displaySeasonLabel } from '../../seasonDisplay';
import { groupLiveRowsByMatch } from '../../stream/groupLiveRowsByMatch';
import { selectMemberHomeLiveRows } from '../../stream/selectMemberHomeLiveRows';
import {
  broadcasterOptions,
  buildReplayRows,
  applyUpcomingWindow,
  buildUpcoming,
  filterLiveGames,
  filterReplayRows,
  filterUpcoming,
  rowsWithPublishedReplay,
  groupByWeek,
  parseGamesTab,
  seasonScopedMatches,
  teamOptions,
  upcomingStageFloor,
  unplayedStatusLabel,
  weekOptions,
  finalScore,
  winningSide,
  type FinalScore,
  type GamesTab,
  type ReplayRow,
} from '../../stream/streamGamesModel';
import { vodExpiryLabel } from '../../stream/vodExpiryLabel';
import type { LscArchiveItem, LscLeague, LscLiveDashboardRow, LscMatch, LscMember } from '../../types';
import CfbMatchupChips from '../CfbMatchupChips';
import ErrorBanner from '../ErrorBanner';
import LiveGameEmbedPanel from '../LiveGameEmbedPanel';
import { resolveMatchArchiveLinkage } from '../MatchesTable';
import MatchupTile, { type MatchupTileSide } from './MatchupTile';
import StatusPill from './StatusPill';
import { StreamSurfaceChrome } from './StreamSurfaceChrome';

const WATCH_BUTTON =
  'inline-flex min-h-12 w-fit items-center gap-2 rounded-stream-md bg-stream-ink px-5 font-stream-display text-sm font-bold uppercase tracking-wide text-stream-ground';

export type StreamGamesPageProps = {
  leagueId: string;
  league: Pick<
    LscLeague,
    | 'game_key'
    | 'active_season_label'
    | 'active_edition'
    | 'active_stage_label'
    | 'streaming_obligation'
    | 'stream_pages'
    | 'can_set_stream_pages'
  > | null;
  isOwner: boolean;
  isCommissioner: boolean;
  isOperator: boolean;
};

function tileSide(label: string | null | undefined, gameKey?: string | null): MatchupTileSide {
  const visual = resolveTeamVisual({
    label: label?.trim() || 'Team',
    gameKey: gameKey || undefined,
  });
  return {
    name: visual.displayName,
    abbreviation: visual.abbreviation,
    tileColors: visual.tileColors,
  };
}

function broadcastLabel(count: number): string | null {
  if (count <= 1) return null;
  return `${count} broadcasts`;
}

function broadcastNameLabels(
  archives: LscArchiveItem[],
  liveRows: LscLiveDashboardRow[],
  members: LscMember[]
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const archive of archives) {
    const sources = archive.sources?.length ? archive.sources : [archive];
    for (const source of sources) {
      const login = source.provider_broadcaster_login?.trim();
      if (!login) continue;
      labels[login] = broadcasterDisplayName({
        login,
        twitchDisplayName: source.provider_broadcaster_display_name,
        members,
      });
    }
  }
  for (const row of liveRows) {
    const login = row.twitch_login?.trim();
    if (!login) continue;
    labels[login] = broadcasterDisplayName({
      login,
      memberName: row.member_name,
      twitchDisplayName: row.display_name,
      members,
    });
  }
  return labels;
}

function broadcasterLine(
  sources: Array<{ name?: string | null; login?: string | null; side?: string | null }>
): string | null {
  const parts = sources
    .map((source) => {
      const login = source.name?.trim() || source.login?.trim();
      if (!login) return null;
      const side = (source.side ?? '').trim().toLowerCase();
      if (sources.length === 1 && side) return `${login} (${side})`;
      return login;
    })
    .filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : null;
}

const StreamGamesPage: React.FC<StreamGamesPageProps> = ({
  leagueId,
  league,
  isOwner,
  isCommissioner,
  isOperator,
}) => {
  const [matches, setMatches] = useState<LscMatch[]>([]);
  const [archives, setArchives] = useState<LscArchiveItem[]>([]);
  const [liveRows, setLiveRows] = useState<LscLiveDashboardRow[]>([]);
  const [members, setMembers] = useState<LscMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLiveStreamKey, setSelectedLiveStreamKey] = useState<string | null>(null);
  const [params] = useSearchParams();
  const memberPreview = params.get('as') === 'member';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const matchData = await listLscMatches(leagueId, { scope: 'active' });
        if (!cancelled) setMatches(matchData);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof LscApiError ? err.message : LEAGUE_SCHEDULES_PAGE_COPY.loadError);
        }
      }
      try {
        const archiveData = await listLscArchive(leagueId, {
          archive_status: 'published',
          group: 'match',
        });
        if (!cancelled) setArchives(archiveData);
      } catch {
        if (!cancelled) setArchives([]);
      }
      try {
        const dashboard = await listLscLiveDashboard(leagueId);
        if (!cancelled) setLiveRows(selectMemberHomeLiveRows(dashboard));
      } catch {
        if (!cancelled) setLiveRows([]);
      }
      try {
        const memberData = await listLscMembers(leagueId);
        if (!cancelled) setMembers(memberData.members);
      } catch {
        if (!cancelled) setMembers([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId, memberPreview]);

  if (loading) {
    return (
      <div
        data-testid="stream-games-skeleton"
        className="stream-scope flex flex-col gap-4"
        aria-busy="true"
      >
        <div className="h-12 rounded-stream-md bg-stream-surface" />
        <div className="h-24 rounded-stream-lg bg-stream-surface" />
        <div className="h-24 rounded-stream-lg bg-stream-surface" />
      </div>
    );
  }

  return (
    <>
      {error ? <ErrorBanner message={error} /> : null}
      <StreamGamesView
        leagueId={leagueId}
        league={league}
        isOwner={isOwner}
        isCommissioner={isCommissioner}
        isOperator={isOperator}
        matches={matches}
        archives={archives}
        liveRows={liveRows}
        members={members}
        selectedLiveStreamKey={selectedLiveStreamKey}
        onSelectLive={setSelectedLiveStreamKey}
      />
    </>
  );
};

export type StreamGamesViewProps = StreamGamesPageProps & {
  matches: LscMatch[];
  archives: LscArchiveItem[];
  liveRows: LscLiveDashboardRow[];
  members?: LscMember[];
  now?: Date;
  selectedLiveStreamKey?: string | null;
  onSelectLive?: (liveStreamKey: string) => void;
};

export const StreamGamesView: React.FC<StreamGamesViewProps> = ({
  leagueId,
  league,
  isOwner,
  isCommissioner,
  isOperator,
  matches,
  archives,
  liveRows,
  members = [],
  now = new Date(),
  selectedLiveStreamKey = null,
  onSelectLive,
}) => {
  const [params, setParams] = useSearchParams();
  const gameKey = league?.game_key;
  const activeSeason = (league?.active_season_label ?? '').trim();
  const seasonMatches = useMemo(
    () => seasonScopedMatches(matches, activeSeason),
    [matches, activeSeason]
  );
  const liveGames = useMemo(
    () => groupLiveRowsByMatch(liveRows, league?.streaming_obligation, now),
    [liveRows, league?.streaming_obligation, now]
  );
  const filters = {
    week: params.get('week') ?? '',
    team: params.get('team') ?? '',
    broadcaster: params.get('broadcaster') ?? '',
  };
  const weekFilter = filters.week === 'all' ? '' : filters.week;
  const scopedFilters = { ...filters, week: weekFilter };
  const previewing = isMemberPreview(params.toString(), { isOwner, isCommissioner, isOperator });
  const operatorView = (isOwner || isCommissioner || isOperator) && !previewing;
  const streamQuery = carryStreamSearch(params.toString(), {
    operator: isOwner || isCommissioner || isOperator,
  });
  const visibleLive = filterLiveGames(liveGames, scopedFilters, gameKey);
  const playedRows = filterReplayRows(
    buildReplayRows(matches, archives, liveGames, activeSeason),
    scopedFilters,
    gameKey
  );
  const replayRows = rowsWithPublishedReplay(playedRows);
  const upcomingFloor = upcomingStageFloor(league?.active_stage_label, liveGames);
  const upcoming = applyUpcomingWindow(
    filterUpcoming(buildUpcoming(matches, archives, liveGames, activeSeason), scopedFilters, gameKey),
    upcomingFloor,
    filters.week
  );
  const tab = parseGamesTab(params.get('tab'), liveGames.length);
  const matchById = useMemo(() => new Map(matches.map((match) => [match.id, match])), [matches]);
  const archivesByMatchId = useMemo(() => groupArchivesByMatchId(archives), [archives]);
  const showDetails = operatorView;

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const weeks = weekOptions(seasonMatches, archives, liveGames);
  const teams = teamOptions(seasonMatches, gameKey);
  const broadcasters = broadcasterOptions(archives, liveRows);
  const broadcasterLabels = broadcastNameLabels(archives, liveRows, members);
  const seasonHeading = activeSeason
    ? displaySeasonLabel(activeSeason, null, league?.active_edition)
    : '';

  const replayGroups = groupByWeek(replayRows, (row) => row.week, 'desc');
  const resultGroups = groupByWeek(playedRows, (row) => row.week, 'desc');
  const upcomingGroups = groupByWeek(upcoming, (row) => row.stage_label ?? '', 'asc');
  const liveGroups = groupByWeek(visibleLive, (game) => game.lead.row.stage_label ?? '', 'asc');

  return (
    <div
      className="stream-scope flex flex-col gap-6 overflow-x-hidden bg-stream-ground font-stream-body text-stream-ink"
      data-testid="stream-games-page"
    >
      <StreamSurfaceChrome
        leagueId={leagueId}
        streamPages={league?.stream_pages}
        canSet={league?.can_set_stream_pages}
        isOperator={isOwner || isCommissioner || isOperator}
      />
      <header>
        {seasonHeading ? (
          <p className="text-sm uppercase tracking-wide text-stream-muted" data-testid="stream-games-season">
            {seasonHeading}
          </p>
        ) : null}
        <h1 className="font-stream-display text-5xl font-bold uppercase leading-none tracking-wide sm:text-6xl">
          Games
        </h1>
      </header>

      <div role="tablist" aria-label="Games" className="flex flex-wrap gap-2" data-testid="stream-games-tabs">
        {(
          [
            ['live', `Live now (${visibleLive.length})`],
            ['upcoming', 'Upcoming'],
            ['replays', 'Replays'],
            ['results', 'Results'],
          ] as Array<[GamesTab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            data-testid={`stream-games-tab-${id}`}
            className={`min-h-11 rounded-stream-md px-4 font-stream-display text-sm font-bold uppercase tracking-wide ${
              tab === id ? 'bg-stream-ink text-stream-ground' : 'border border-stream-line text-stream-muted'
            }`}
            onClick={() => updateParam('tab', id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid max-w-lsc-narrow gap-3 sm:grid-cols-3" data-testid="stream-games-filters">
        <FilterSelect
          label="Week"
          value={filters.week}
          testId="stream-games-week-filter"
          options={tab === 'upcoming' ? ['all', ...weeks] : weeks}
          labels={tab === 'upcoming' ? { all: 'All weeks' } : undefined}
          allLabel={tab === 'upcoming' ? 'This week and later' : 'All weeks'}
          onChange={(value) => updateParam('week', value)}
        />
        <FilterSelect
          label="Team"
          value={filters.team}
          testId="stream-games-team-filter"
          options={teams}
          allLabel="All teams"
          onChange={(value) => updateParam('team', value)}
        />
        <FilterSelect
          label="Broadcaster"
          value={filters.broadcaster}
          testId="stream-games-broadcaster-filter"
          options={broadcasters}
          labels={broadcasterLabels}
          allLabel="All broadcasters"
          onChange={(value) => updateParam('broadcaster', value)}
        />
      </div>

      <div className="lsc-split-when-wide flex flex-col gap-6 overflow-x-hidden" data-testid="stream-games-list" data-layout="cards">
        {tab === 'live' ? (
          liveGroups.length === 0 ? (
            <Empty copy="Nothing is live right now." />
          ) : (
            liveGroups.map((group) => (
              <WeekSection key={group.week} week={group.week}>
                {group.items.map((game) => {
                  const lead = game.lead.row;
                  const streamKey = resolveLiveStreamKey(mapLiveDashboardRowToLeagueGame(lead));
                  const liveMatch = game.competitionMatchId
                    ? matchById.get(game.competitionMatchId) ?? null
                    : null;
                  const count = game.sources.length;
                  const status = [
                    game.lead.elapsedMinutes != null ? `Live for ${game.lead.elapsedMinutes} min` : null,
                    broadcastLabel(count),
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <GameRow
                      key={game.competitionMatchId ?? streamKey}
                      away={lead.away_team}
                      home={lead.home_team}
                      gameKey={gameKey}
                      score={finalScore(liveMatch)}
                      detail={broadcasterLine(
                        game.sources.map((source) => ({
                          name: broadcasterDisplayName({
                            login: source.row.twitch_login,
                            memberName: source.row.member_name,
                            twitchDisplayName: source.row.display_name,
                            members,
                          }),
                          login: source.row.twitch_login,
                          side: source.row.broadcaster_side,
                        }))
                      )}
                      status={
                        <>
                          <StatusPill variant="live" label="Live" />
                          {status ? <span>{status}</span> : null}
                        </>
                      }
                      action={
                        <button
                          type="button"
                          className={WATCH_BUTTON}
                          data-testid="stream-games-watch"
                          aria-label={`Watch ${lead.away_team?.trim() || 'Away'} at ${lead.home_team?.trim() || 'Home'}`}
                          onClick={() => onSelectLive?.(streamKey)}
                        >
                          <Play className="h-4 w-4 fill-current" aria-hidden />
                          Watch
                        </button>
                      }
                      extra={
                        selectedLiveStreamKey === streamKey ? (
                          <div data-testid="stream-games-live-player">
                            <LiveGameEmbedPanel game={mapLiveDashboardRowToLeagueGame(lead)} />
                          </div>
                        ) : null
                      }
                      showDetails={showDetails}
                      match={liveMatch}
                      archive={null}
                      archivesByMatchId={archivesByMatchId}
                    />
                  );
                })}
              </WeekSection>
            ))
          )
        ) : null}

        {tab === 'upcoming' ? (
          upcomingGroups.length === 0 ? (
            <Empty copy="No games still to play." />
          ) : (
            upcomingGroups.map((group) => (
              <WeekSection key={group.week} week={group.week}>
                {group.items.map((row) => (
                  <GameRow
                    key={row.id}
                    away={row.away_side_label}
                    home={row.home_side_label}
                    gameKey={gameKey}
                    score={null}
                    detail={null}
                    status={<span>{unplayedStatusLabel(row.stage_label, upcomingFloor)}</span>}
                    showDetails={showDetails}
                    match={row}
                    archive={null}
                    archivesByMatchId={archivesByMatchId}
                  />
                ))}
              </WeekSection>
            ))
          )
        ) : null}

        {tab === 'replays' || tab === 'results' ? (
          (tab === 'replays' ? replayGroups : resultGroups).length === 0 ? (
            <Empty
              copy={
                matches.length === 0 && archives.length === 0
                  ? operatorView
                    ? LEAGUE_SCHEDULES_PAGE_COPY.emptyState
                    : LEAGUE_SCHEDULES_PAGE_COPY.memberEmptyState
                  : tab === 'replays'
                    ? filters.week || filters.team || filters.broadcaster
                      ? 'No replays match these filters.'
                      : 'No replays yet.'
                    : 'No results yet.'
              }
            />
          ) : (
            (tab === 'replays' ? replayGroups : resultGroups).map((group) => (
              <WeekSection key={group.week} week={group.week}>
                {group.items.map((row) => (
                  <ReplayGameRow
                    key={row.key}
                    row={row}
                    leagueId={leagueId}
                    gameKey={gameKey}
                    now={now}
                    streamQuery={streamQuery}
                    showDetails={showDetails}
                    members={members}
                    archivesByMatchId={archivesByMatchId}
                  />
                ))}
              </WeekSection>
            ))
          )
        ) : null}
      </div>
    </div>
  );
};

function ReplayGameRow({
  row,
  leagueId,
  gameKey,
  now,
  streamQuery,
  showDetails,
  members,
  archivesByMatchId,
}: {
  row: ReplayRow;
  leagueId: string;
  gameKey?: string | null;
  now: Date;
  streamQuery: string;
  showDetails: boolean;
  members: LscMember[];
  archivesByMatchId: Map<string, LscArchiveItem[]>;
}) {
  const archive = row.archive;
  const count = archive?.sources?.length ?? (archive ? 1 : 0);
  const expiry = archive ? vodExpiryLabel(archive.vod_created_at, archive.playback_status, now) : null;
  const duration = formatReplayDuration(archive?.duration_seconds);
  const broadcasts = broadcastLabel(count);
  return (
    <GameRow
      away={row.away}
      home={row.home}
      gameKey={gameKey}
      score={row.score}
      detail={
        archive
          ? broadcasterLine(
              (archive.sources?.length ? archive.sources : [archive]).map((source) => ({
                name: broadcasterDisplayName({
                  login: source.provider_broadcaster_login,
                  twitchDisplayName: source.provider_broadcaster_display_name,
                  members,
                }),
                login: source.provider_broadcaster_login,
                side: source.broadcaster_side,
              }))
            )
          : null
      }
      status={
        <>
          {duration ? <span>{duration}</span> : null}
          {expiry?.kind === 'may_expire' ? <StatusPill variant="expire" label={expiry.text} /> : null}
          {expiry?.kind === 'unavailable' ? <StatusPill variant="expire" label="Video unavailable" /> : null}
          {broadcasts ? <span>{broadcasts}</span> : null}
        </>
      }
      action={
        archive ? (
          <Link
            to={`${lscPath(
              `/leagues/${encodeURIComponent(leagueId)}/archive/${encodeURIComponent(archive.id)}`
            )}${streamQuery}`}
            state={{ watchFrom: 'games' }}
            className={WATCH_BUTTON}
            data-testid="stream-games-watch"
          >
            <Play className="h-4 w-4 fill-current" aria-hidden />
            Watch
          </Link>
        ) : null
      }
      showDetails={showDetails}
      match={row.match}
      archive={archive}
      archivesByMatchId={archivesByMatchId}
    />
  );
}

function GameRow({
  away,
  home,
  gameKey,
  score,
  detail,
  status,
  action,
  extra,
  showDetails,
  match,
  archive,
  archivesByMatchId,
}: {
  away?: string | null;
  home?: string | null;
  gameKey?: string | null;
  score: FinalScore | null;
  detail: string | null;
  status: React.ReactNode;
  action?: React.ReactNode;
  extra?: React.ReactNode;
  showDetails: boolean;
  match: LscMatch | null;
  archive: LscArchiveItem | null;
  archivesByMatchId: Map<string, LscArchiveItem[]>;
}) {
  const [open, setOpen] = useState(false);
  const winner = score ? winningSide(score) : null;
  const awayName = away?.trim() || 'Away';
  const homeName = home?.trim() || 'Home';
  const linked = match ? archivesByMatchId.get(match.id) ?? [] : archive ? [archive] : [];
  const linkage = match ? resolveMatchArchiveLinkage(match, linked) : archive ? 'watch' : 'none';
  const identityMatch: LscMatch = match ?? {
    id: archive?.id ?? 'archive',
    away_identity_value: archive?.match_summary?.away_identity_value,
    home_identity_value: archive?.match_summary?.home_identity_value,
  };

  return (
    <article
      className="flex w-full min-w-0 flex-col gap-3 rounded-stream-md border border-stream-line bg-stream-surface p-3"
      data-testid="stream-games-row"
    >
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
        <MatchupTile size="compact" away={tileSide(awayName, gameKey)} home={tileSide(homeName, gameKey)} />
        <div className="min-w-0 flex-1">
          <p className="font-stream-display text-xl font-medium uppercase leading-tight" data-testid="stream-games-matchup">
            <ScoreSide name={awayName} points={score?.away} tone={scoreTone(winner, 'away')} />
            <span className="px-1 text-stream-muted">@</span>
            <ScoreSide name={homeName} points={score?.home} tone={scoreTone(winner, 'home')} />
          </p>
          {gameKey === 'cfb' ? (
            <CfbMatchupChips awayLabel={awayName} homeLabel={homeName} separator="@" size="sm" showLabels />
          ) : null}
          {detail ? <p className="text-sm text-stream-muted">{detail}</p> : null}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-stream-muted">{status}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {action}
          {showDetails ? (
            <button
              type="button"
              className="min-h-12 rounded-stream-md border border-stream-line px-4 text-sm text-stream-muted"
              aria-expanded={open}
              data-testid="stream-games-details-toggle"
              onClick={() => setOpen((value) => !value)}
            >
              {open ? 'Hide details' : 'Details'}
            </button>
          ) : null}
        </div>
      </div>
      {extra}
      {open ? (
        <dl className="grid gap-2 text-sm text-stream-muted sm:grid-cols-2" data-testid="stream-games-details">
          <div>
            <dt>Identities</dt>
            <dd>{formatMatchIdentities(identityMatch, archive)}</dd>
          </div>
          <div>
            <dt>Archive</dt>
            <dd>
              {linkage === 'watch'
                ? LEAGUE_SCHEDULES_PAGE_COPY.archivePublishedLabel
                : linkage === 'linked'
                  ? LEAGUE_SCHEDULES_PAGE_COPY.archiveLinkedLabel
                  : '—'}
            </dd>
          </div>
          <div>
            <dt>Lifecycle</dt>
            <dd className="capitalize">{match?.lifecycle_status ?? 'active'}</dd>
          </div>
          <div>
            <dt>Scheduled</dt>
            <dd>{match?.scheduled_at ? new Date(match.scheduled_at).toLocaleString() : '—'}</dd>
          </div>
        </dl>
      ) : null}
    </article>
  );
}

function scoreTone(winner: 'away' | 'home' | null, side: 'away' | 'home'): 'win' | 'lose' | 'even' {
  if (!winner) return 'even';
  return winner === side ? 'win' : 'lose';
}

function ScoreSide({
  name,
  points,
  tone,
}: {
  name: string;
  points?: number;
  tone: 'win' | 'lose' | 'even';
}) {
  const className =
    tone === 'win'
      ? 'font-bold text-stream-ink'
      : tone === 'lose'
        ? 'font-medium text-stream-muted'
        : 'font-medium text-stream-ink';
  return (
    <span className={className} data-winning={tone === 'win' ? 'true' : undefined} data-score-tone={tone}>
      <span>{name}</span>
      {points != null ? <span>{` ${points}`}</span> : null}
    </span>
  );
}

function WeekSection({ week, children }: { week: string; children: React.ReactNode }) {
  return (
    <section data-testid="stream-games-week" data-week={week}>
      <h2 className="mb-3 font-stream-display text-lg font-bold uppercase tracking-wide text-stream-muted">
        {week || 'Week'}
      </h2>
      <div className="lsc-split-grid">{children}</div>
    </section>
  );
}

function FilterSelect({
  label,
  value,
  options,
  labels,
  allLabel,
  testId,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  labels?: Record<string, string>;
  allLabel: string;
  testId: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-stream-muted">{label}</span>
      <select
        className="min-h-11 rounded-stream-md border border-stream-line bg-stream-surface px-3"
        value={value}
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels?.[option] ?? option}
          </option>
        ))}
      </select>
    </label>
  );
}

function Empty({ copy }: { copy: string }) {
  return (
    <p className="text-sm text-stream-muted" data-testid="stream-games-empty">
      {copy}
    </p>
  );
}

export default StreamGamesPage;
